import * as React from 'react'
import { DefaultContent } from 'react-content-engine'

import './CalendarMonth.scss'

export type NumberOrRange = number | [number, number | null]

interface CalendarMonthProps {
  year: number
  month: number
  dayCell: JSX.Element
  emptyDayCell?: JSX.Element
  allowSelection?: boolean
  allowRange?: boolean
  selectedDay?: number | null
  cheapestDay?: number | null
  range?: number
  onDaySelect?: (numberOrRange: NumberOrRange) => any
}

interface CalendarMonthState {
  hovering?: number
}

class CalendarMonth extends React.Component<
  CalendarMonthProps,
  CalendarMonthState
> {
  public static defaultProps: Partial<CalendarMonthProps> = {
    emptyDayCell: <div />,
    allowSelection: false,
    allowRange: false,
  }

  constructor(props: CalendarMonthProps) {
    super(props)
  }

  public onSelect(day: number) {
    if (this.props.allowSelection && this.props.onDaySelect) {
      if (this.props.allowRange) {
        if (
          this.props.range ||
          this.props.selectedDay === null ||
          this.props.selectedDay === undefined
        ) {
          // either a range has already been selected,
          // which in that case should be reset,
          // or nothing has been selected:
          // set the selected day as the earliest extremity of the new range
          return this.props.onDaySelect([day, null])
        }
        // in this case the range hasn't been selected, so give it back to the controlled
        const firstDay = Math.min(this.props.selectedDay, day)
        const lastDay = Math.max(this.props.selectedDay, day)
        return this.props.onDaySelect([firstDay, lastDay - firstDay + 1])
      }
      // case when range is not allowed, just give the day back to the controlled
      return this.props.onDaySelect(day)
    }
  }

  public getDayClass(day: number) {
    const classes = ['month-day']
    if (this.props.allowSelection) {
      classes.push('is-selectable')

      if (
        this.props.allowRange &&
        (this.props.range === null || this.props.range === undefined) &&
        this.props.selectedDay &&
        this.state &&
        this.state.hovering
      ) {
        const min = Math.min(this.state.hovering, this.props.selectedDay)
        const max = Math.max(this.state.hovering, this.props.selectedDay)
        if (min < day && day < max) {
          classes.push('future-range')
        }
      }

      if (this.props.cheapestDay && this.props.cheapestDay === day) {
        classes.push('is-cheapest')
      }

      if (this.props.selectedDay) {
        if (this.props.selectedDay === day) {
          classes.push('is-selected')
        }
        if (this.props.allowRange && this.props.range) {
          const lastDay = this.props.selectedDay + this.props.range - 1
          if (this.props.selectedDay < day && day < lastDay) {
            classes.push('in-range')
          } else if (day === lastDay) {
            classes.push('is-selected')
          }
        }
      }
    }
    return classes.join(' ')
  }

  public getDayCell(day: number) {
    return () => React.cloneElement(this.props.dayCell, { day })
  }

  public renderDay(day: number) {
    const currentDay = day
    const DayCell = this.getDayCell(day)
    return (
      <td
        key={day + 'x'}
        className={this.getDayClass(day)}
        onClick={() => this.onSelect(currentDay)}
        onMouseEnter={() => this.state?.hovering !== currentDay && this.setState({ hovering: currentDay })}
      >
        <DayCell />
      </td>
    )
  }

  public renderWeeks() {
    // each row corresponds to a week
    const rows: JSX.Element[] = []
    // get the offset of days in the first row up until the first of the month
    let firstDayOffset =
      new Date(this.props.year, this.props.month - 1, 1).getDay() - 1
    if (firstDayOffset === -1) {
      // correction for when there is only one day in the first week
      firstDayOffset = 6
    }
    // calculate the number of days on that month
    const numberDays = new Date(this.props.year, this.props.month, 0).getDate()
    // start counting before the first day (to fill in first row completely
    let dayCount = 1 - firstDayOffset

    let cells: JSX.Element[] = []
    while (dayCount <= numberDays) {
      if (cells.length === 7) {
        // if the week is filled, push it to the `rows` array and start a new week
        rows.push(<tr key={dayCount}>{cells}</tr>)
        cells = []
      }

      if (dayCount <= 0) {
        // offset days of first row
        cells.push(
          <td key={dayCount} className="non-month-day before">
            {this.props.emptyDayCell}
          </td>,
        )
      } else if (dayCount === numberDays) {
        // fill in the last week
        cells.push(this.renderDay(dayCount))
        for (let i = 0, iMax = 7 - cells.length; i < iMax; i++) {
          cells.push(
            <td key={dayCount + i} className="non-month-day after">
              {this.props.emptyDayCell}
            </td>,
          )
        }
      } else {
        // push actual day cells
        cells.push(this.renderDay(dayCount))
      }
      dayCount++
    }
    // push the last week
    rows.push(
      <tr key={dayCount} className="week">
        {cells}
      </tr>,
    )
    return rows
  }

  public render() {
    return (
      <div className="calendar-month">
        <table>
          <thead>
            <tr>
              <th>
                <DefaultContent contentKey="monday-short">MON</DefaultContent>
              </th>
              <th>
                <DefaultContent contentKey="tuesday-short">TUE</DefaultContent>
              </th>
              <th>
                <DefaultContent contentKey="wednesday-short">
                  WED
                </DefaultContent>
              </th>
              <th>
                <DefaultContent contentKey="thursday-short">THU</DefaultContent>
              </th>
              <th>
                <DefaultContent contentKey="friday-short">FRI</DefaultContent>
              </th>
              <th>
                <DefaultContent contentKey="saturday-short">SAT</DefaultContent>
              </th>
              <th>
                <DefaultContent contentKey="sunday-short">SUN</DefaultContent>
              </th>
            </tr>
          </thead>
          <tbody>{this.renderWeeks()}</tbody>
        </table>
      </div>
    )
  }
}

export default CalendarMonth
